import * as React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { cn } from "../../lib/cn"
import { useDismiss } from "../../hooks/useDismiss"

export interface MenuItem {
    id: string
    label: React.ReactNode
    icon?: React.ReactNode
    /** Internal route (react-router Link). */
    to?: string
    /** Router state to pass with `to` (e.g. { triggerFilters: true }). */
    state?: unknown
    /** External link (plain anchor). */
    href?: string
    /** Action item (rendered as a button). */
    onSelect?: () => void
}

export type MenuEntry = MenuItem | { type: "divider" }

export interface MenuButtonProps {
    /** Render-prop for the trigger; spread `triggerProps` and attach `ref` to a <button>. */
    trigger: (props: {
        open: boolean
        ref: React.Ref<HTMLButtonElement>
        triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement>
    }) => React.ReactNode
    items: MenuEntry[]
    align?: "start" | "end"
    /** Accessible name for the menu. */
    menuLabel: string
    /** Extra classes for the floating panel (e.g. width). */
    panelClassName?: string
}

function isDivider(entry: MenuEntry): entry is { type: "divider" } {
    return (entry as { type?: string }).type === "divider"
}

/**
 * Accessible menu button: a trigger that opens a `role="menu"` of `menuitem`
 * links/actions. Keyboard: Arrow/Home/End roam (roving real focus — items are
 * focusable), Enter/Space activate (links navigate natively, so new-tab/cmd-click
 * still work), Esc/Tab/outside-click close (Esc returns focus to the trigger).
 * Composes the shared `useDismiss` core.
 */
export function MenuButton({ trigger, items, align = "start", menuLabel, panelClassName }: MenuButtonProps) {
    const [open, setOpenState] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const itemRefs = useRef<(HTMLElement | null)[]>([])

    // UI-46: the `onOpenChange` prop was removed with the header auto-hide. Its ONLY
    // consumers were Header's isMenuOpen/isGymsMenuOpen mirrors, which existed solely
    // to keep the auto-hiding header revealed while a menu was open. Nothing observes
    // menu open-state from outside any more, so the prop is gone rather than left as
    // a callerless API whose own docblock cited the deleted mechanism.
    const setOpen = useCallback((next: boolean) => {
        setOpenState(next)
    }, [])

    const menuItemCount = items.filter(e => !isDivider(e)).length

    useDismiss({ open, onClose: () => setOpen(false), triggerRef, panelRef })

    // Move real DOM focus to the active item whenever it changes while open.
    useEffect(() => {
        if (open) itemRefs.current[activeIndex]?.focus()
    }, [open, activeIndex])

    const openMenu = useCallback((index = 0) => { setActiveIndex(index); setOpen(true) }, [])
    const close = useCallback(() => setOpen(false), [])

    const onMenuKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "ArrowDown": e.preventDefault(); setActiveIndex(i => (i + 1) % menuItemCount); break
            case "ArrowUp": e.preventDefault(); setActiveIndex(i => (i - 1 + menuItemCount) % menuItemCount); break
            case "Home": e.preventDefault(); setActiveIndex(0); break
            case "End": e.preventDefault(); setActiveIndex(menuItemCount - 1); break
            case "Tab": setOpen(false); break // let focus leave the menu naturally
            default: break
        }
    }

    const triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
        "aria-haspopup": "menu",
        "aria-expanded": open,
        onClick: () => (open ? close() : openMenu(0)),
        onKeyDown: (e) => {
            if (open) return
            if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") { e.preventDefault(); openMenu(0) }
            else if (e.key === "ArrowUp") { e.preventDefault(); openMenu(menuItemCount - 1) }
        },
    }

    // H5 / UI-33: row padding comes from the shared menu-row-* spacing tokens
    // (tailwind.config.js) — the same pair the Header drawer rows use, so the
    // dropdown/drawer padding split cannot recur. This moved these rows from
    // px-4 py-4 (16/16) onto the drawer's 20/12. min-h-[44px] enforces the tap
    // target IN the primitive: the old py-4 supplied it implicitly, and the
    // slimmer padding would otherwise leave the floor to whatever text size a
    // future consumer's context happens to inherit (review-caught hazard).
    const itemClass = "w-full flex items-center gap-3 min-h-[44px] px-menu-row-x py-menu-row-y text-text-primary hover:bg-brand-primary-subtle focus:bg-brand-primary-subtle focus:outline-none transition-colors text-left"

    let renderedItemIndex = -1
    return (
        <div className="relative">
            {trigger({ open, ref: triggerRef, triggerProps })}
            {open && (
                <div
                    ref={panelRef}
                    role="menu"
                    aria-label={menuLabel}
                    aria-orientation="vertical"
                    onKeyDown={onMenuKeyDown}
                    className={cn(
                        "absolute top-full mt-2 z-50 overflow-hidden rounded-xl border border-border-default bg-surface-base shadow-dropdown",
                        align === "end" ? "right-0" : "left-0",
                        panelClassName,
                    )}
                >
                    {items.map((entry, i) => {
                        if (isDivider(entry)) {
                            return <div key={`divider-${i}`} role="separator" className="border-t border-border-default" />
                        }
                        renderedItemIndex++
                        const idx = renderedItemIndex
                        const tabIndex = idx === activeIndex ? 0 : -1
                        const setRef = (el: HTMLElement | null) => { itemRefs.current[idx] = el }
                        const onActivate = () => { entry.onSelect?.(); close() }
                        const content = <>{entry.icon}<span className="truncate">{entry.label}</span></>
                        if (entry.to) {
                            return (
                                <Link key={entry.id} to={entry.to} state={entry.state as object | undefined} role="menuitem" tabIndex={tabIndex} ref={setRef as React.Ref<HTMLAnchorElement>} onClick={onActivate} className={itemClass}>
                                    {content}
                                </Link>
                            )
                        }
                        if (entry.href) {
                            return (
                                <a key={entry.id} href={entry.href} role="menuitem" tabIndex={tabIndex} ref={setRef as React.Ref<HTMLAnchorElement>} onClick={onActivate} className={itemClass}>
                                    {content}
                                </a>
                            )
                        }
                        return (
                            <button key={entry.id} type="button" role="menuitem" tabIndex={tabIndex} ref={setRef as React.Ref<HTMLButtonElement>} onClick={onActivate} className={itemClass}>
                                {content}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
